import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import JsonLd from '@/components/feature/JsonLd';
import { SITE_URL, SITE_NAME, FAVICON_URL } from '@/lib/seo';

const articleBgUrl = 'https://readdy.ai/api/search-image?query=Minimal%20workspace%20with%20laptop%20spreadsheet%20screen%20warm%20wooden%20desk%20soft%20natural%20lighting%20coffee%20cup%20notebook%20pen%20clean%20aesthetic%20editorial%20photography%20warm%20beige%20tones%20no%20people&width=1920&height=600&seq=article-hero-01&orientation=landscape';

const guideJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'Article',
  headline: 'The Complete AllChinaBuy Spreadsheet Guide for 2026',
  description:
    'Everything you need to know about finding, using, and maximizing AllChinaBuy spreadsheet resources for smarter shopping.',
  image: articleBgUrl,
  datePublished: '2026-08-26',
  dateModified: '2026-08-26',
  author: {
    '@type': 'Organization',
    name: SITE_NAME,
    url: `${SITE_URL}/`,
  },
  publisher: {
    '@type': 'Organization',
    name: SITE_NAME,
    logo: {
      '@type': 'ImageObject',
      url: FAVICON_URL,
    },
  },
  mainEntityOfPage: `${SITE_URL}/allchinabuy-spreadsheet-guide`,
};

export default function AllChinaBuySpreadsheetGuide() {
  const { t } = useTranslation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-background-50">
      <JsonLd data={guideJsonLd} />
      <Navbar />

      {/* Article Hero */}
      <section className="relative h-[400px] md:h-[500px] overflow-hidden">
        <img src={articleBgUrl} alt="Workspace with spreadsheet" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-primary-950/60 via-primary-950/70 to-primary-950/90" />
        <div className="absolute inset-0 flex items-center">
          <div className="w-full px-4 md:px-8 lg:px-12">
            <div className="max-w-4xl">
              <nav className="text-sm text-background-300 mb-4">
                <a href="/" className="hover:text-background-50 transition-colors">Home</a>
                <span className="mx-2">/</span>
                <a href="/" className="hover:text-background-50 transition-colors">Guides</a>
                <span className="mx-2">/</span>
                <span className="text-background-50">AllChinaBuy Spreadsheet Guide</span>
              </nav>
              <h1 className="font-heading text-3xl md:text-5xl font-semibold text-background-50 leading-tight mb-4">
                The Complete AllChinaBuy Spreadsheet Guide for 2026
              </h1>
              <p className="text-base md:text-lg text-background-200 max-w-2xl">
                Everything you need to know about finding, using, and maximizing AllChinaBuy spreadsheet resources for smarter shopping.
              </p>
              <div className="flex items-center gap-4 mt-6 text-sm text-background-300">
                <span>August 26, 2026</span>
                <span>·</span>
                <span>12 min read</span>
                <span>·</span>
                <span className="bg-accent-500/20 text-accent-300 px-3 py-1 rounded-full">Buying Guide</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Article Content */}
      <article className="w-full px-4 md:px-8 lg:px-12 py-12 md:py-20">
        <div className="max-w-4xl mx-auto">
          {/* Table of Contents */}
          <div className="bg-background-100 rounded-xl p-6 md:p-8 mb-12 border border-background-200/70">
            <h3 className="font-heading text-lg font-semibold text-foreground-800 mb-4">Table of Contents</h3>
            <ul className="space-y-2">
              {[
                'What Is an AllChinaBuy Spreadsheet?',
                'Why Use a Spreadsheet for AllChinaBuy Shopping?',
                'How to Read an AllChinaBuy Spreadsheet',
                'Finding Reliable Seller Information',
                'Understanding Product Categories',
                'QC Photos and Quality Control',
                'Shipping Methods and Cost Estimation',
                'Common Beginner Mistakes to Avoid',
                'AllChinaBuy Spreadsheet vs Other Platforms',
                'FAQ and Quick Tips',
              ].map((item, i) => (
                <li key={i}>
                  <a href={`#section-${i + 1}`} className="text-sm text-foreground-600 hover:text-accent-600 transition-colors flex items-center gap-2">
                    <span className="w-6 h-6 rounded-full bg-primary-50 text-primary-600 text-xs flex items-center justify-center font-medium">{i + 1}</span>
                    {item}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Section 1 */}
          <section id="section-1" className="mb-12 md:mb-16">
            <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-900 mb-6">
              What Is an AllChinaBuy Spreadsheet?
            </h2>
            <p className="text-base md:text-lg text-foreground-600 leading-relaxed mb-6">
              An <strong>AllChinaBuy spreadsheet</strong> is a curated, organized collection of product links, seller information, pricing data, and buying resources specifically designed for shoppers using the AllChinaBuy platform. Unlike browsing marketplaces directly, a spreadsheet consolidates valuable information into an easy-to-scan format, allowing you to compare products, evaluate sellers, and plan your purchases efficiently.
            </p>
            <p className="text-base md:text-lg text-foreground-600 leading-relaxed mb-6">
              These spreadsheets typically include product names, image thumbnails, seller ratings, price ranges, sizing information, and direct links to product pages. For anyone serious about finding the best deals on fashion, footwear, accessories, and streetwear through AllChinaBuy, a well-maintained spreadsheet is an invaluable resource that saves hours of research time.
            </p>
            <p className="text-base md:text-lg text-foreground-600 leading-relaxed mb-6">
              The beauty of the <a href="/" className="text-accent-600 hover:text-accent-700 font-medium underline decoration-accent-300 underline-offset-4">AllChinaBuy Spreadsheet resource hub</a> is that it brings together community-verified finds, experienced buyer recommendations, and regularly updated product listings all in one place. Whether you are looking for the latest sneaker releases, designer-inspired clothing, or unique accessories, the spreadsheet format makes discovery simple and systematic.
            </p>
            <div className="bg-accent-50/50 border border-accent-200/50 rounded-xl p-5 md:p-6">
              <p className="text-sm text-accent-800 font-medium mb-2">Key Takeaway</p>
              <p className="text-sm text-foreground-600">
                An AllChinaBuy spreadsheet transforms chaotic marketplace browsing into an organized shopping experience. It is the difference between wandering aimlessly and shopping with a plan.
              </p>
            </div>
          </section>

          {/* Section 2 */}
          <section id="section-2" className="mb-12 md:mb-16">
            <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-900 mb-6">
              Why Use a Spreadsheet for AllChinaBuy Shopping?
            </h2>
            <p className="text-base md:text-lg text-foreground-600 leading-relaxed mb-6">
              Shopping through agent platforms like AllChinaBuy can be overwhelming. Chinese marketplaces contain millions of listings, quality varies dramatically between sellers, and language barriers can complicate the buying process. A dedicated spreadsheet solves these problems by providing pre-filtered, community-tested recommendations.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 mb-6">
              {[
                { title: 'Time Efficiency', desc: 'Skip hours of searching through unfiltered marketplace results. Spreadsheets present only vetted products and trusted sellers.' },
                { title: 'Price Comparison', desc: 'See multiple seller options for similar products side by side, making it easy to identify the best value.' },
                { title: 'Quality Assurance', desc: 'Community-reviewed entries mean someone has already ordered and verified the product quality.' },
                { title: 'Organized Categories', desc: 'Products are grouped by type — shoes, hoodies, accessories — so you find what you need quickly.' },
              ].map((item, i) => (
                <div key={i} className="bg-background-50 border border-background-200/70 rounded-xl p-5">
                  <h3 className="font-medium text-foreground-800 mb-2">{item.title}</h3>
                  <p className="text-sm text-foreground-500">{item.desc}</p>
                </div>
              ))}
            </div>
            <p className="text-base md:text-lg text-foreground-600 leading-relaxed">
              For European shoppers especially, where shipping costs and import duties can significantly impact the final price, having advance knowledge of product weights, dimensions, and estimated shipping fees helps avoid unpleasant surprises at checkout. The <a href="/" className="text-accent-600 hover:text-accent-700 font-medium underline decoration-accent-300 underline-offset-4">AllChinaBuy Spreadsheet hub</a> includes shipping estimation resources alongside product listings.
            </p>
          </section>

          {/* Section 3 */}
          <section id="section-3" className="mb-12 md:mb-16">
            <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-900 mb-6">
              How to Read an AllChinaBuy Spreadsheet
            </h2>
            <p className="text-base md:text-lg text-foreground-600 leading-relaxed mb-6">
              Most AllChinaBuy spreadsheets follow a standard column structure that becomes intuitive once you understand the format. Here is what each column typically represents and how to interpret the data effectively.
            </p>
            <div className="overflow-x-auto mb-6">
              <table className="w-full text-sm border border-background-200/70 rounded-xl overflow-hidden">
                <thead>
                  <tr className="bg-primary-50">
                    <th className="text-left px-4 py-3 font-medium text-foreground-700">Column</th>
                    <th className="text-left px-4 py-3 font-medium text-foreground-700">What It Means</th>
                    <th className="text-left px-4 py-3 font-medium text-foreground-700">How to Use It</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-background-200/70">
                  {[
                    { col: 'Product Name', mean: 'The item description or popular name', use: 'Search or filter to find specific items' },
                    { col: 'Seller/Store', mean: 'The Taobao/Weidian seller name', use: 'Check seller ratings before ordering' },
                    { col: 'Price (CNY)', mean: 'Original product price in Chinese Yuan', use: 'Calculate total cost including agent fees' },
                    { col: 'Category', mean: 'Product type: shoes, clothing, accessories', use: 'Filter by category to browse efficiently' },
                    { col: 'Link', mean: 'Direct product URL', use: 'Copy to AllChinaBuy to place your order' },
                    { col: 'QC Photos', mean: 'Whether quality check photos are available', use: 'Always request QC before shipping' },
                    { col: 'Last Updated', mean: 'When the listing was last verified', use: 'Prefer recently updated entries' },
                    { col: 'Size Info', mean: 'Sizing notes or fit recommendations', use: 'Check before ordering to avoid returns' },
                  ].map((row, i) => (
                    <tr key={i} className="bg-background-50 hover:bg-background-100 transition-colors">
                      <td className="px-4 py-3 font-medium text-foreground-800">{row.col}</td>
                      <td className="px-4 py-3 text-foreground-600">{row.mean}</td>
                      <td className="px-4 py-3 text-foreground-500">{row.use}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-base md:text-lg text-foreground-600 leading-relaxed">
              Pro tip: When you find a product you want to purchase, copy the link directly from the spreadsheet and paste it into your AllChinaBuy purchase form. Always double-check that the seller is still active and the product is in stock before submitting your order.
            </p>
          </section>

          {/* Section 4 */}
          <section id="section-4" className="mb-12 md:mb-16">
            <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-900 mb-6">
              Finding Reliable Seller Information
            </h2>
            <p className="text-base md:text-lg text-foreground-600 leading-relaxed mb-6">
              Seller reliability is the single most important factor in a successful AllChinaBuy purchase. A great product from an unreliable seller can still result in disappointment — delayed shipping, incorrect items, or poor quality control. The spreadsheet format helps because it includes community feedback about seller experiences.
            </p>
            <p className="text-base md:text-lg text-foreground-600 leading-relaxed mb-6">
              Look for these indicators of a trustworthy seller: high store ratings (usually displayed as diamonds or crowns on the marketplace), a large number of transactions, positive review photos from real buyers, and responsive customer service. Spreadsheets often highlight sellers who have been repeatedly tested by the community.
            </p>
            <div className="bg-background-100 rounded-xl p-6 md:p-8 mb-6 border border-background-200/70">
              <h3 className="font-heading text-lg font-semibold text-foreground-800 mb-4">Red Flags to Watch For</h3>
              <ul className="space-y-3">
                {[
                  'Sellers with no transaction history or very few reviews',
                  'Prices significantly lower than comparable listings (often indicates bait-and-switch)',
                  'Products with no buyer photos in reviews',
                  'Sellers who refuse to provide additional photos when requested',
                  'Listings that use stock photos from other brands without disclosure',
                ].map((flag, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <i className="ri-error-warning-line text-accent-500 mt-0.5" />
                    <span className="text-sm md:text-base text-foreground-600">{flag}</span>
                  </li>
                ))}
              </ul>
            </div>
            <p className="text-base md:text-lg text-foreground-600 leading-relaxed">
              Remember, the <a href="/" className="text-accent-600 hover:text-accent-700 font-medium underline decoration-accent-300 underline-offset-4">AllChinaBuy resource hub</a> is designed to help you navigate these decisions with confidence. Use the spreadsheet data as a starting point, but always verify current seller status before placing large orders.
            </p>
          </section>

          {/* Section 5 */}
          <section id="section-5" className="mb-12 md:mb-16">
            <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-900 mb-6">
              Understanding Product Categories
            </h2>
            <p className="text-base md:text-lg text-foreground-600 leading-relaxed mb-6">
              AllChinaBuy spreadsheets organize products into logical categories that mirror how shoppers think about fashion and accessories. Understanding these categories helps you navigate the spreadsheet efficiently and discover products you might not have considered.
            </p>
            <p className="text-base md:text-lg text-foreground-600 leading-relaxed mb-6">
              The main categories you will encounter include: <strong>Shoes</strong> (sneakers, boots, dress shoes), <strong>Clothing</strong> (hoodies, t-shirts, jackets, pants, jerseys), <strong>Accessories</strong> (bags, belts, hats, jewelry), and <strong>Specialty Items</strong> (perfumes, sets, underwear). Each category has its own spreadsheet tab or filter section, making it easy to focus your search.
            </p>
            <p className="text-base md:text-lg text-foreground-600 leading-relaxed">
              For the best experience, start with the category that matches your current shopping goal, but do not hesitate to browse adjacent categories. Many shoppers discover unexpected finds by exploring related sections. The <a href="/" className="text-accent-600 hover:text-accent-700 font-medium underline decoration-accent-300 underline-offset-4">AllChinaBuy Spreadsheet homepage</a> provides direct access to all category listings.
            </p>
          </section>

          {/* Section 6 */}
          <section id="section-6" className="mb-12 md:mb-16">
            <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-900 mb-6">
              QC Photos and Quality Control
            </h2>
            <p className="text-base md:text-lg text-foreground-600 leading-relaxed mb-6">
              Quality Control (QC) photos are your best defense against receiving disappointing products. When your items arrive at the AllChinaBuy warehouse, the platform takes photos of each product so you can verify quality before approving international shipping. This step is crucial because it is your last chance to return or exchange items before they leave China.
            </p>
            <p className="text-base md:text-lg text-foreground-600 leading-relaxed mb-6">
              A good AllChinaBuy spreadsheet will indicate which products have been QC-verified by the community. This means previous buyers have received the item, photographed it, and confirmed it matches the listing description. When you see a "QC Verified" tag in a spreadsheet, you can order with significantly more confidence.
            </p>
            <div className="bg-secondary-50/50 border border-secondary-200/50 rounded-xl p-5 md:p-6 mb-6">
              <p className="text-sm text-secondary-800 font-medium mb-2">QC Photo Checklist</p>
              <ul className="text-sm text-foreground-600 space-y-2">
                <li>· Check material quality against listing photos</li>
                <li>· Verify stitching, logos, and print placement</li>
                <li>· Confirm sizing by measuring against size chart</li>
                <li>· Look for any visible defects or discoloration</li>
                <li>· Compare color accuracy under warehouse lighting</li>
              </ul>
            </div>
            <p className="text-base md:text-lg text-foreground-600 leading-relaxed">
              Never skip the QC step, even for inexpensive items. The cost of catching a problem at the warehouse is negligible compared to receiving an unusable product after weeks of shipping time.
            </p>
          </section>

          {/* Section 7 */}
          <section id="section-7" className="mb-12 md:mb-16">
            <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-900 mb-6">
              Shipping Methods and Cost Estimation
            </h2>
            <p className="text-base md:text-lg text-foreground-600 leading-relaxed mb-6">
              Shipping represents a significant portion of your total cost when using AllChinaBuy, so understanding your options is essential for budgeting. The platform offers multiple shipping lines, each with different speed, cost, and reliability profiles. Your choice should depend on your budget, timeline, and the type of products you are shipping.
            </p>
            <p className="text-base md:text-lg text-foreground-600 leading-relaxed mb-6">
              Common shipping options include: <strong>EMS</strong> (reliable, moderate speed, good for most items), <strong>DHL</strong> (fastest, premium cost, excellent tracking), <strong>EUB</strong> (budget-friendly, slower, weight limits apply), and various <strong>specialized lines</strong> for specific regions or product types. The best choice varies by destination country.
            </p>
            <p className="text-base md:text-lg text-foreground-600 leading-relaxed mb-6">
              For European shoppers, be particularly aware of VAT and import duty thresholds. Many countries have de minimis values below which no duties apply, but these vary significantly. Consolidating multiple items into a single shipment can sometimes reduce per-item shipping costs, though it may increase the chance of customs inspection.
            </p>
            <p className="text-base md:text-lg text-foreground-600 leading-relaxed">
              Use the <a href="/" className="text-accent-600 hover:text-accent-700 font-medium underline decoration-accent-300 underline-offset-4">AllChinaBuy resource hub</a> to estimate shipping before you buy. Entering product dimensions and weights into the shipping calculator helps you avoid budget surprises at the final checkout stage.
            </p>
          </section>

          {/* Section 8 */}
          <section id="section-8" className="mb-12 md:mb-16">
            <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-900 mb-6">
              Common Beginner Mistakes to Avoid
            </h2>
            <p className="text-base md:text-lg text-foreground-600 leading-relaxed mb-6">
              Even experienced shoppers make mistakes when using agent platforms. Here are the most common errors beginners encounter and how to avoid them, based on community feedback and our own testing.
            </p>
            <div className="space-y-4 mb-6">
              {[
                { title: 'Ignoring Size Charts', desc: 'Asian sizing often runs smaller than European or American sizes. Always measure a garment you already own and compare it to the provided size chart. Never assume your usual size will fit.' },
                { title: 'Skipping QC Photos', desc: 'The warehouse QC step exists to protect you. Approving shipment without checking photos is the fastest way to receive something you do not want. Always review carefully.' },
                { title: 'Underestimating Shipping Costs', desc: 'Product price is only part of the total cost. Shipping, agent fees, and potential customs duties can double or triple your final expense. Calculate everything before ordering.' },
                { title: 'Ordering from Unverified Sellers', desc: 'Community-verified sellers have a track record. New sellers with no reviews might offer great prices, but the risk of poor quality or non-shipment is significantly higher.' },
                { title: 'Not Declaring Proper Value', desc: 'Customs declarations matter. Under-declaring can lead to confiscation; over-declaring leads to unnecessary duties. Research your country\'s specific rules for personal imports.' },
              ].map((mistake, i) => (
                <div key={i} className="bg-background-50 border border-background-200/70 rounded-xl p-5 md:p-6">
                  <h3 className="font-medium text-foreground-800 mb-2 flex items-center gap-2">
                    <i className="ri-close-circle-line text-accent-500" />
                    {mistake.title}
                  </h3>
                  <p className="text-sm md:text-base text-foreground-600">{mistake.desc}</p>
                </div>
              ))}
            </div>
            <p className="text-base md:text-lg text-foreground-600 leading-relaxed">
              Learning from others mistakes is the fastest way to become a confident AllChinaBuy shopper. The community behind the spreadsheet resources shares experiences precisely so newcomers can avoid these common pitfalls.
            </p>
          </section>

          {/* Section 9 */}
          <section id="section-9" className="mb-12 md:mb-16">
            <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-900 mb-6">
              AllChinaBuy Spreadsheet vs Other Platforms
            </h2>
            <p className="text-base md:text-lg text-foreground-600 leading-relaxed mb-6">
              How does AllChinaBuy compare to other popular shopping agent platforms? While each service has its strengths, understanding the differences helps you choose the right platform for your specific needs.
            </p>
            <div className="overflow-x-auto mb-6">
              <table className="w-full text-sm border border-background-200/70 rounded-xl overflow-hidden">
                <thead>
                  <tr className="bg-primary-50">
                    <th className="text-left px-4 py-3 font-medium text-foreground-700">Feature</th>
                    <th className="text-left px-4 py-3 font-medium text-foreground-700">AllChinaBuy</th>
                    <th className="text-left px-4 py-3 font-medium text-foreground-700">Superbuy</th>
                    <th className="text-left px-4 py-3 font-medium text-foreground-700">CSSBuy</th>
                    <th className="text-left px-4 py-3 font-medium text-foreground-700">Sugargoo</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-background-200/70">
                  {[
                    { feat: 'Interface Ease', acb: 'Moderate', sb: 'High', css: 'Moderate', sg: 'High' },
                    { feat: 'Shipping Rates', acb: 'Competitive', sb: 'Premium', css: 'Budget', sg: 'Moderate' },
                    { feat: 'QC Photo Quality', acb: 'Good', sb: 'Excellent', css: 'Good', sg: 'Good' },
                    { feat: 'Product Spreadsheet', acb: 'Strong', sb: 'Moderate', css: 'Moderate', sg: 'Moderate' },
                    { feat: 'Customer Support', acb: 'Good', sb: 'Excellent', css: 'Moderate', sg: 'Good' },
                    { feat: 'European Focus', acb: 'Growing', sb: 'Strong', css: 'Moderate', sg: 'Growing' },
                  ].map((row, i) => (
                    <tr key={i} className="bg-background-50 hover:bg-background-100 transition-colors">
                      <td className="px-4 py-3 font-medium text-foreground-800">{row.feat}</td>
                      <td className="px-4 py-3 text-foreground-600">{row.acb}</td>
                      <td className="px-4 py-3 text-foreground-600">{row.sb}</td>
                      <td className="px-4 py-3 text-foreground-600">{row.css}</td>
                      <td className="px-4 py-3 text-foreground-600">{row.sg}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-base md:text-lg text-foreground-600 leading-relaxed">
              AllChinaBuy excels in providing a straightforward, cost-effective experience with growing community resources. For shoppers who value organized spreadsheet resources and competitive shipping rates, it represents an excellent balance of functionality and value. Visit the <a href="/" className="text-accent-600 hover:text-accent-700 font-medium underline decoration-accent-300 underline-offset-4">AllChinaBuy Spreadsheet hub</a> to see current product offerings.
            </p>
          </section>

          {/* Section 10 */}
          <section id="section-10" className="mb-12 md:mb-16">
            <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-900 mb-6">
              FAQ and Quick Tips
            </h2>
            <div className="space-y-4">
              {[
                { q: 'How often are AllChinaBuy spreadsheets updated?', a: 'Quality spreadsheets are updated weekly or monthly, depending on the community maintainers. Always check the last-updated date before making purchasing decisions based on older listings.' },
                { q: 'Can I trust product links from spreadsheets?', a: 'Links are community-verified, but marketplace listings change constantly. Always verify the product is still active and in stock before ordering. A link that worked last month may be dead today.' },
                { q: 'What is the best way to budget for an AllChinaBuy order?', a: 'Calculate: product price + agent service fee (usually 5-10%) + domestic shipping in China + international shipping + potential customs fees. Add a 15% buffer for unexpected costs.' },
                { q: 'Should I use a spreadsheet or browse directly?', a: 'For specific items you know you want, spreadsheets save time. For discovery and browsing, direct marketplace exploration with search filters can yield unexpected finds. Use both approaches.' },
                { q: 'How do I handle returns or exchanges?', a: 'Contact your agent immediately if QC photos reveal problems. Once you approve shipping, returns become expensive and complicated. The QC stage is your primary protection.' },
              ].map((faq, i) => (
                <div key={i} className="bg-background-100 rounded-xl p-5 md:p-6 border border-background-200/70">
                  <h3 className="font-medium text-foreground-800 mb-2">Q: {faq.q}</h3>
                  <p className="text-sm md:text-base text-foreground-600">A: {faq.a}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Internal Linking Section */}
          <section className="bg-accent-50/30 border border-accent-200/50 rounded-xl p-6 md:p-8 mb-12">
            <h3 className="font-heading text-lg font-semibold text-foreground-800 mb-4">Related Resources</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <a href="/" className="flex items-center gap-2 text-sm text-accent-700 hover:text-accent-800 transition-colors">
                <i className="ri-arrow-right-line text-accent-500" />
                Back to AllChinaBuy Spreadsheet Hub
              </a>
              <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="flex items-center gap-2 text-sm text-accent-700 hover:text-accent-800 transition-colors">
                <i className="ri-external-link-line text-accent-500" />
                Visit Main Website — Kaspreadsheet
              </a>
              <a href="/" className="flex items-center gap-2 text-sm text-accent-700 hover:text-accent-800 transition-colors">
                <i className="ri-arrow-right-line text-accent-500" />
                AllChinaBuy Shipping Cost Guide
              </a>
              <a href="/" className="flex items-center gap-2 text-sm text-accent-700 hover:text-accent-800 transition-colors">
                <i className="ri-arrow-right-line text-accent-500" />
                Is AllChinaBuy Legit? Safety Review
              </a>
              <a href="/" className="flex items-center gap-2 text-sm text-accent-700 hover:text-accent-800 transition-colors">
                <i className="ri-arrow-right-line text-accent-500" />
                AllChinaBuy Coupon & Discount Codes
              </a>
              <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="flex items-center gap-2 text-sm text-accent-700 hover:text-accent-800 transition-colors">
                <i className="ri-external-link-line text-accent-500" />
                Browse Products on Kaspreadsheet
              </a>
            </div>
          </section>

          {/* Final CTA */}
          <div className="text-center bg-primary-950 rounded-xl p-8 md:p-12">
            <h3 className="font-heading text-xl md:text-2xl font-semibold text-background-50 mb-4">
              Ready to Start Using AllChinaBuy?
            </h3>
            <p className="text-sm md:text-base text-background-300 mb-6 max-w-xl mx-auto">
              Explore our curated spreadsheet resources, find verified products, and shop with confidence. Your next great find is just a click away.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="px-6 py-3 bg-accent-500 text-background-50 rounded-lg font-medium text-sm hover:bg-accent-600 transition-colors inline-flex items-center gap-2"
              >
                Visit Kaspreadsheet Now
                <i className="ri-external-link-line" />
              </a>
              <a
                href="/"
                className="px-6 py-3 bg-background-50/10 backdrop-blur text-background-50 border border-background-200/30 rounded-lg font-medium text-sm hover:bg-background-50/20 transition-colors inline-flex items-center gap-2"
              >
                Back to Homepage
                <i className="ri-arrow-left-line" />
              </a>
            </div>
          </div>
        </div>
      </article>

      <Footer />
    </div>
  );
}